import React, { useEffect, useMemo } from 'react';

import { Handle, Position } from '@xyflow/react';

import { ActionIcon } from 'assets/icons/action-icon';

import { actionSchema } from '../../../summary-sidebar/sections/action-section';
import { useWorkflowBuilderContext } from '../../../wizard/builder-context';
import { actionTypeLabels, type ActionNodeData } from '../models';
import { AddNodeHandle } from '../shared/add-node-handle';

import { NodeActions } from './node-actions';
import { NodeRowContainer, StyledBaseNode, StyledDescription, StyledDivider, StyledLabel } from './styles';

type Props = {
  data: ActionNodeData;
  id: string;
};

export function CustomActionNode({ data, id }: Props) {
  const [builderState] = useWorkflowBuilderContext();
  const { selectedNode, isReadOnly } = builderState;
  const isCurrentlySelectedNode = selectedNode?.id === id;
  const actionTypeLabel = actionTypeLabels[data.actionType];
  const isValid = useMemo(() => actionSchema.isValidSync(data), [data]);
  const isConnected = builderState.edges.some((edge) => edge.source === id);
  const { stats, isDeliverabilityMetricsLoading } = data;

  useEffect(() => {
    if (data.reportIsValid) {
      data.reportIsValid(isValid);
    }
  }, [isValid, data]);

  const getActionTypeLabel = () => {
    if (data.config?.pointBoostConfig?.points) {
      return `${actionTypeLabel} - ${String(data.config?.pointBoostConfig?.points)}`;
    }
    return actionTypeLabel;
  };

  return (
    <StyledBaseNode isError={!isValid} isSelected={isCurrentlySelectedNode}>
      <Handle position={Position.Top} type='target' />
      <NodeRowContainer>
        <ActionIcon />
        <StyledLabel>Action</StyledLabel>
      </NodeRowContainer>
      <StyledDescription>{getActionTypeLabel()}</StyledDescription>
      {!isReadOnly && <StyledDivider variant='compact' />}
      <NodeActions
        deliverabilityStats={data.deliverabilityStats}
        isDeliverabilityMetricsLoading={isDeliverabilityMetricsLoading}
        nodeId={id}
        nodeType={data.actionType}
        showAnalytics={data.showAnalytics}
        stats={stats}
      />
      {data.onAddNode && (
        <AddNodeHandle isConnected={isConnected} type='next' onAdd={(nodeType) => data.onAddNode!(id, nodeType)} />
      )}
      <Handle position={Position.Bottom} type='source' />
    </StyledBaseNode>
  );
}
